import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API, useAuth } from '@/App';
import axios from 'axios';
import { Check, X } from 'lucide-react';

const FLAG_EMOJI = {
  Australia: '\u{1F1E6}\u{1F1FA}', Canada: '\u{1F1E8}\u{1F1E6}', 'United Kingdom': '\u{1F1EC}\u{1F1E7}',
  'United States': '\u{1F1FA}\u{1F1F8}', 'New Zealand': '\u{1F1F3}\u{1F1FF}', Germany: '\u{1F1E9}\u{1F1EA}',
  Ireland: '\u{1F1EE}\u{1F1EA}', Singapore: '\u{1F1F8}\u{1F1EC}', India: '\u{1F1EE}\u{1F1F3}',
};

const STATUS_STEPS = {
  visa: ['submitted', 'admin_review', 'agent_assigned', 'filed', 'result'],
  visa_only: ['submitted', 'admin_review', 'agent_assigned', 'filed', 'result'],
  admission: ['submitted', 'admin_review', 'sent_to_institutions', 'offers', 'coe'],
  both: ['submitted', 'admin_review', 'sent_to_institutions', 'offers', 'visa'],
};

const STATUS_LABELS = {
  submitted: 'Submitted', admin_review: 'Review', sent_to_institutions: 'Institutions',
  agent_assigned: 'Agent', filed: 'Filed', result: 'Result',
  offers: 'Offers', coe: 'CoE', visa: 'Visa',
};

const STATUS_INDEX = {
  submitted: 0, admin_review: 1, pending_review: 1,
  sent_to_institutions: 2, broadcast: 2, agent_assigned: 2,
  offers: 3, coe: 4, filed: 3, result: 4,
  visa_filing: 3, visa_submitted: 3, visa_processing: 4, visa_approved: 4,
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return 'now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

export default function LiveTrackingCard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [visible, setVisible] = useState(false);
  const [countdown, setCountdown] = useState(10);
  const [dismissed, setDismissed] = useState(false);
  const timerRef = useRef(null);
  const pausedRef = useRef(false);

  useEffect(() => {
    if (!user || user.role !== 'student') return;
    const shown = sessionStorage.getItem('tracking_shown');
    if (shown) return;

    axios.get(`${API}/tracking/active`).then(res => {
      if (res.data?.has_active) {
        setData(res.data);
        setVisible(true);
        sessionStorage.setItem('tracking_shown', 'true');
      }
    }).catch(() => {});
  }, [user]);

  // Countdown timer
  useEffect(() => {
    if (!visible || dismissed) return;
    timerRef.current = setInterval(() => {
      if (pausedRef.current) return;
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setDismissed(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [visible, dismissed]);

  const dismiss = () => {
    setDismissed(true);
    clearInterval(timerRef.current);
  };

  const goToDashboard = () => {
    clearInterval(timerRef.current);
    navigate('/dashboard');
  };

  if (!visible || dismissed || !data) return null;

  const app = data.application;
  const msgs = data.messages || [];
  const appType = app.type || 'admission';
  const steps = STATUS_STEPS[appType] || STATUS_STEPS.admission;
  const currentIdx = STATUS_INDEX[app.status] ?? 0;
  const country = app.destination_country || app.study_country || '';
  const flag = FLAG_EMOJI[country] || '';
  const label = appType.includes('visa')
    ? `${country} ${app.visa_type || 'Visa'}`
    : `${country} · ${app.field_of_study || ''}`;
  const statusLabel = app.status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const latestMsg = msgs[0];

  return (
    <div
      className={`fixed top-16 left-1/2 -translate-x-1/2 z-50 w-[92%] max-w-md transition-all duration-500 ${dismissed ? 'opacity-0 -translate-y-4' : 'opacity-100 translate-y-0'}`}
      onMouseEnter={() => { pausedRef.current = true; }}
      onMouseLeave={() => { pausedRef.current = false; }}
      data-testid="live-tracking-card"
    >
      <div className="bg-slate-900 rounded-2xl shadow-2xl border border-slate-700 overflow-hidden">
        {/* Card Body — tap goes to dashboard */}
        <div className="p-4 cursor-pointer" onClick={goToDashboard}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-lg">{flag}</span>
              <span className="text-white text-sm font-bold truncate max-w-[200px]">{label}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-emerald-400 text-[10px] font-bold">{statusLabel}</span>
              <span className="text-slate-500 text-[10px]">{timeAgo(app.updated_at || app.created_at)}</span>
              <button onClick={(e) => { e.stopPropagation(); dismiss(); }}
                className="w-5 h-5 rounded-full bg-slate-800 flex items-center justify-center text-slate-500 hover:text-white"
                data-testid="tracking-dismiss">
                <X className="h-3 w-3" />
              </button>
            </div>
          </div>

          {/* Mini Timeline */}
          <div className="flex items-center gap-0.5 mb-2">
            {steps.map((s, i) => (
              <div key={s} className="flex items-center flex-1">
                <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                  i < currentIdx ? 'bg-emerald-500' : i === currentIdx ? 'bg-emerald-400 ring-2 ring-emerald-400/30' : 'bg-slate-700'
                }`}>
                  {i < currentIdx && <Check className="h-1.5 w-1.5 text-white m-auto" />}
                </div>
                {i < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-0.5 ${i < currentIdx ? 'bg-emerald-500' : 'bg-slate-700'}`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between">
            {steps.map((s, i) => (
              <span key={s} className={`text-[7px] font-medium flex-1 text-center ${
                i <= currentIdx ? 'text-emerald-400' : 'text-slate-600'
              }`}>{STATUS_LABELS[s]}</span>
            ))}
          </div>

          {/* Latest message preview */}
          {latestMsg && (
            <div className="mt-2 flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-[#2F80ED] shrink-0" />
              <p className="text-[10px] text-slate-400 truncate">{latestMsg.content}</p>
            </div>
          )}

          {/* Expand hint */}
          <div className="flex items-center justify-center mt-1">
            <span className="text-[8px] text-slate-600">Tap to view dashboard</span>
          </div>
        </div>

        {/* Countdown bar */}
        <div className="h-0.5 bg-slate-800">
          <div
            className="h-full bg-emerald-500 transition-all duration-1000 ease-linear"
            style={{ width: `${(countdown / 10) * 100}%` }}
            data-testid="tracking-countdown-bar"
          />
        </div>
      </div>
    </div>
  );
}
